import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from "react";
import { CSSLengthSliderEditor, CSSNumberUnitEditor, SDropdownV3 } from "../..";
import { SField } from "../../components";
import { startCase } from "es-toolkit";
export const StackPropertyEditor = ({ value, onChangeValue, readOnly, onClickValue })=>{
    const xDistributionExtras = [
        "stretch",
        "space-evenly",
        "space-around",
        "space-between"
    ];
    const yDistributionExtras = [
        "stretch",
        "space-evenly",
        "space-around",
        "space-between"
    ];
    const zDistributionExtras = [
        "stretch"
    ];
    const axisMap = {
        x: "Horizontal",
        y: "Vertical",
        z: "Layered"
    };
    const axisOptions = [
        "x",
        "y",
        "z"
    ].map((axis)=>({
            label: axisMap[axis],
            value: axis
        }));
    const yAxisAlignmentLabelOverrides = {
        start: "Top",
        end: "Bottom"
    };
    const yAxisDistributionLabelOverrides = {
        start: "Left",
        end: "Right"
    };
    const alignmentOptions = useMemo(()=>{
        const baseline = value?.axis == "x" ? [
            "baseline"
        ] : [];
        return [
            "start",
            "center",
            "end",
            "stretch",
            ...baseline
        ].map((crossAxisAlignment)=>({
                label: (value?.axis != "y" ? yAxisAlignmentLabelOverrides[crossAxisAlignment] : yAxisDistributionLabelOverrides[crossAxisAlignment]) || startCase(crossAxisAlignment),
                value: crossAxisAlignment
            }));
    }, [
        value?.axis
    ]);
    const wrapOptions = [
        "nowrap",
        "wrap",
        "wrap-reverse"
    ].map((wrap)=>({
            label: wrap == "nowrap" ? "Don't Wrap" : startCase(wrap),
            value: wrap
        }));
    const reverseOptions = [
        "true",
        "false"
    ].map((reverse)=>({
            label: reverse == "true" ? "Yes" : "No",
            value: reverse
        }));
    // scroll: T.setEnum(new Set(["none", "horizontal", "vertical", "both"])).optional(),
    // scrollBehavior: T.setEnum(new Set(["normal", "paging", "snap"])).optional(),
    // snapPosition: T.setEnum(new Set(["start", "center", "end"])).optional(),
    const scrollOptions = [
        "none",
        "normal",
        "paging",
        "infinite"
    ].map((scroll)=>({
            label: scroll == "none" ? "No Scroll" : startCase(scroll),
            value: scroll
        }));
    const snapPositionOptions = [
        "start",
        "center",
        "end"
    ].map((snapPosition)=>({
            label: startCase(snapPosition),
            value: snapPosition
        }));
    const autoAdvancePageOptions = [
        "enabled",
        "disabled"
    ].map((autoAdvancePage)=>({
            label: startCase(autoAdvancePage),
            value: autoAdvancePage
        }));
    const setProps = (newProps)=>{
        let props = {
            type: "property-stack",
            axis: "x",
            reverse: false,
            crossAxisAlignment: "start",
            mainAxisDistribution: "start",
            wrap: "nowrap",
            gap: "8px",
            scroll: "none",
            snapPosition: "center",
            autoAdvancePageEnabled: undefined,
            autoAdvancePageDelayS: undefined,
            infiniteScrollSpeed: undefined
        };
        if (newProps?.axis && newProps.axis == "z" && (xDistributionExtras.includes(value?.mainAxisDistribution || "") || yDistributionExtras.includes(value?.mainAxisDistribution || "")) && !zDistributionExtras.includes(value?.mainAxisDistribution || "")) {
            newProps.mainAxisDistribution = "start";
        }
        // if (newProps?.axis && newProps.axis == "y" && value?.mainAxisDistribution == "stretch") {
        //   newProps.mainAxisDistribution = "start"
        // }
        if (newProps?.axis && newProps.axis != "x" && value?.crossAxisAlignment == "baseline") {
            newProps.crossAxisAlignment = "start";
        }
        onChangeValue && onChangeValue({
            ...props,
            ...value,
            ...newProps
        });
    };
    const onInputChange = (e)=>{
        e.preventDefault();
        setProps({
            gap: `${e.target.value}px`
        });
    };
    const distributionOptions = useMemo(()=>{
        return [
            "start",
            "center",
            "end",
            ...value?.axis == "z" ? zDistributionExtras : [],
            ...value?.axis == "x" ? xDistributionExtras : [],
            ...value?.axis == "y" ? yDistributionExtras : []
        ].map((mainAxisDistribution)=>{
            if (value?.axis != "z" && mainAxisDistribution == "stretch") {
                return {
                    label: "Fill Equally",
                    value: mainAxisDistribution
                };
            }
            if (value?.axis == "y") {
                return {
                    label: yAxisAlignmentLabelOverrides[mainAxisDistribution] || startCase(mainAxisDistribution),
                    value: mainAxisDistribution
                };
            }
            return {
                label: yAxisDistributionLabelOverrides[mainAxisDistribution] || startCase(mainAxisDistribution),
                value: mainAxisDistribution
            };
        });
    }, [
        value?.axis
    ]);
    // converts a string like 7px or 10% to {value, unit} (not knowing what the units are)
    const parseLength = (length)=>{
        if (!length) {
            return {
                type: "css-length",
                value: 0,
                unit: "px"
            };
        }
        const match = length.match(/^(\d+)(.*)$/);
        if (!match) {
            return {
                type: "css-length",
                value: 0,
                unit: "px"
            };
        }
        return {
            type: "css-length",
            value: parseInt(match[1]),
            unit: match[2] || "px"
        };
    };
    const getSizeNeededToScroll = (val)=>{
        if (!val) {
            return "";
        }
        if (val.axis === "x") {
            return "width";
        }
        if (val.axis === "y") {
            return "height";
        }
        return "";
    };
    // const [gap, setGap] = useState(parseLength(value?.gap))
    const gap = useMemo(()=>parseLength(value?.gap), [
        value?.gap
    ]);
    return /*#__PURE__*/ _jsx("div", {
        onClick: onClickValue,
        children: /*#__PURE__*/ _jsx("div", {
            className: readOnly ? "pointer-events-none" : "",
            children: /*#__PURE__*/ _jsx(SField, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col gap-2",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "grid grid-cols-1 gap-2",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "grid grid-cols-2 gap-3 items-center",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            children: "Axis"
                                        }),
                                        /*#__PURE__*/ _jsx(SDropdownV3, {
                                            borderless: false,
                                            options: axisOptions,
                                            value: axisOptions.find((option)=>option.value == value?.axis),
                                            onChange: (axis)=>{
                                                setProps({
                                                    axis: axis.value
                                                });
                                            }
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: `flex ${value?.axis == "y" ? "flex-col-reverse" : "flex-col"} gap-2`,
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "grid grid-cols-2 gap-3 items-center",
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    children: value?.axis == "y" ? "Horizontal" : "Vertical"
                                                }),
                                                /*#__PURE__*/ _jsx(SDropdownV3, {
                                                    borderless: false,
                                                    options: alignmentOptions,
                                                    value: alignmentOptions.find((option)=>option.value == value?.crossAxisAlignment),
                                                    onChange: (crossAxisAlignment)=>{
                                                        setProps({
                                                            crossAxisAlignment: crossAxisAlignment.value
                                                        });
                                                    }
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "grid grid-cols-2 gap-3 items-center",
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    children: value?.axis == "y" ? "Vertical" : "Horizontal"
                                                }),
                                                /*#__PURE__*/ _jsx(SDropdownV3, {
                                                    borderless: false,
                                                    options: distributionOptions,
                                                    value: distributionOptions.find((option)=>option.value == value?.mainAxisDistribution),
                                                    onChange: (mainAxisDistribution)=>{
                                                        setProps({
                                                            mainAxisDistribution: mainAxisDistribution.value
                                                        });
                                                    }
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        value?.axis != "z" && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "grid grid-cols-2 gap-3 items-center",
                                    children: [
                                        "Spacing",
                                        /*#__PURE__*/ _jsx(CSSLengthSliderEditor, {
                                            value: gap,
                                            onChangeValue: (gap)=>{
                                                setProps({
                                                    gap: `${gap?.value}${gap?.unit}`
                                                });
                                            }
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "grid grid-cols-2 gap-3 items-center",
                                    children: [
                                        "Wrap",
                                        /*#__PURE__*/ _jsx(SDropdownV3, {
                                            borderless: false,
                                            options: wrapOptions,
                                            value: wrapOptions.find((option)=>option.value == value?.wrap),
                                            onChange: (wrap)=>{
                                                setProps({
                                                    wrap: wrap.value
                                                });
                                            }
                                        })
                                    ]
                                })
                            ]
                        }),
                        value?.axis != "z" && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "grid grid-cols-2 gap-3 items-center",
                                    children: [
                                        "Scroll",
                                        /*#__PURE__*/ _jsx(SDropdownV3, {
                                            borderless: false,
                                            options: scrollOptions,
                                            value: scrollOptions.find((option)=>option.value == value?.scroll) || scrollOptions[0],
                                            onChange: (scroll)=>{
                                                setProps({
                                                    scroll: scroll.value
                                                });
                                            }
                                        })
                                    ]
                                }),
                                value?.scroll === "paging" && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "grid grid-cols-2 gap-3 items-center",
                                            children: [
                                                "Snap Position",
                                                /*#__PURE__*/ _jsx(SDropdownV3, {
                                                    borderless: false,
                                                    options: snapPositionOptions,
                                                    value: snapPositionOptions.find((option)=>option.value == value?.snapPosition) || snapPositionOptions[1],
                                                    onChange: (snapPosition)=>{
                                                        setProps({
                                                            snapPosition: snapPosition.value
                                                        });
                                                    }
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "grid grid-cols-2 gap-3 items-center",
                                            children: [
                                                "Auto Paging",
                                                /*#__PURE__*/ _jsx(SDropdownV3, {
                                                    borderless: false,
                                                    options: autoAdvancePageOptions,
                                                    value: autoAdvancePageOptions.find((option)=>option.value == value?.autoAdvancePageEnabled) || autoAdvancePageOptions[1],
                                                    onChange: (autoAdvancePageEnabled)=>{
                                                        setProps({
                                                            autoAdvancePageEnabled: autoAdvancePageEnabled.value
                                                        });
                                                    }
                                                })
                                            ]
                                        }),
                                        value?.autoAdvancePageEnabled === "enabled" && /*#__PURE__*/ _jsxs("div", {
                                            className: "grid grid-cols-2 gap-3 items-center",
                                            children: [
                                                "Paging Delay",
                                                /*#__PURE__*/ _jsx(CSSNumberUnitEditor, {
                                                    label: "Delay",
                                                    unit: "s",
                                                    placeholder: "5",
                                                    value: {
                                                        type: "css-number-unit",
                                                        value: value?.autoAdvancePageDelayS || "",
                                                        unit: "s"
                                                    },
                                                    onChangeValue: (delayMs)=>{
                                                        if (typeof delayMs === "undefined") {
                                                            setProps({
                                                                autoAdvancePageDelayS: undefined
                                                            });
                                                            return;
                                                        }
                                                        setProps({
                                                            autoAdvancePageDelayS: delayMs.value
                                                        });
                                                    }
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                value?.scroll === "infinite" && /*#__PURE__*/ _jsxs("div", {
                                    className: "grid grid-cols-2 gap-3 items-center",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            children: "Infinite Scroll Speed"
                                        }),
                                        /*#__PURE__*/ _jsx(CSSNumberUnitEditor, {
                                            label: "Speed",
                                            unit: "px/frame",
                                            placeholder: "3",
                                            value: {
                                                type: "css-number-unit",
                                                value: value?.infiniteScrollSpeed?.toString() || "3",
                                                unit: "px/frame"
                                            },
                                            onChangeValue: (speed)=>{
                                                if (typeof speed === "undefined" || speed.value === "") {
                                                    setProps({
                                                        infiniteScrollSpeed: 0
                                                    }) // Set to 0 when input is empty
                                                    ;
                                                    return;
                                                }
                                                const parsedSpeed = parseFloat(speed.value);
                                                if (isNaN(parsedSpeed)) {
                                                    setProps({
                                                        infiniteScrollSpeed: 0
                                                    }) // Set to 0 for invalid input
                                                    ;
                                                } else {
                                                    setProps({
                                                        infiniteScrollSpeed: parsedSpeed
                                                    });
                                                }
                                            }
                                        })
                                    ]
                                }),
                                value?.scroll && value?.scroll !== "none" && /*#__PURE__*/ _jsxs("p", {
                                    className: "text-sm opacity-50",
                                    children: [
                                        "Be sure to set a ",
                                        getSizeNeededToScroll(value),
                                        " under Size for scrolling to work."
                                    ]
                                })
                            ]
                        })
                    ]
                })
            })
        })
    });
};
